/**
 * Kontroll → Per ansatt (#275, V7.5.6): one employee list in place of the record queue, never above it. Each
 * count opens exactly that person's records in that status (the prototype's direct links); zero is plain
 * text. «Åpne» is the one door to all of the person's records; the name and the facts under it (uten
 * stempling, venter på svar, pågår nå, ferdig kontrollert) are plain text with their icons (owner 2026-11-01,
 * DIFF D148, D151). Filters: Alle statuser, Gjenstående arbeid, Ferdig kontrollert (kontroll.ts perPerson
 * decides «ferdig»); sort Mest å avklare or Navn; 16 people a page.
 */
import { T } from '@/i18n';
import { PersonLabel } from '@/ui/PersonLabel';
import { Icon } from '@/ui/icons';
import { Link } from 'react-router-dom';
import { Pagination } from './KontrollQueue';
import type { PersonRow } from './kontroll';

export const PEOPLE_PAGE = 16;
export type PeopleFilter = 'alle' | 'gjenstar' | 'ferdig';
export type Drill = 'handling' | 'klare' | 'godkjent' | 'alle';

export function KontrollPeople({
  rows,
  all,
  filter,
  sort,
  page,
  nameOf,
  subOf,
  onFilter,
  onSort,
  onPage,
  onDrill,
  worktime,
}: {
  /** the people after the filter, sorted */
  rows: PersonRow[];
  /** everyone in the scope, for «x av y ferdig» */
  all: PersonRow[];
  filter: PeopleFilter;
  sort: 'prioritet' | 'navn';
  page: number;
  nameOf: (id: number) => string;
  /** team and employee number under the name */
  subOf: (id: number) => string;
  onFilter: (f: PeopleFilter) => void;
  onSort: (s: 'prioritet' | 'navn') => void;
  onPage: (p: number) => void;
  onDrill: (id: number, d: Drill) => void;
  /** the address of the per-person work-time page for this period */
  worktime?: string;
}) {
  const pages = Math.max(1, Math.ceil(rows.length / PEOPLE_PAGE));
  const pg = Math.min(page, pages - 1);
  const shown = rows.slice(pg * PEOPLE_PAGE, pg * PEOPLE_PAGE + PEOPLE_PAGE);
  const done = all.filter((p) => p.complete).length;
  const count = (p: PersonRow, d: Drill, n: number, label: string, tone: string, of?: number) =>
    n > 0 ? (
      <button
        type="button"
        className={`kcount ${tone}`}
        onClick={() => onDrill(p.id, d)}
        aria-label={`${nameOf(p.id)}, ${label}: ${of != null ? T('{a} av {b}', { a: n, b: of }) : n}`}
      >
        <span>{n}</span>
        {of != null && <small>{T('av {n}', { n: of })}</small>}
      </button>
    ) : (
      <span className="kzero">0{of != null && <small>{T('av {n}', { n: of })}</small>}</span>
    );
  return (
    <section className="kpeople" aria-labelledby="kpeople-h">
      <div className="kqh">
        <div>
          <h2 id="kpeople-h" tabIndex={-1}>
            {T('Kontroll per ansatt')} <span className="kqn">{rows.length}</span>
          </h2>
          <p className="kqs" data-testid="people-done">
            {T('{a} av {b} ansatte ferdig kontrollert i utvalget', { a: done, b: all.length })}
          </p>
        </div>
        <div className="kqf">
          <label>
            <span className="sr">{T('Kontrollstatus for ansatte')}</span>
            <select
              className="inp kpf"
              value={filter}
              data-testid="people-filter"
              aria-label={T('Kontrollstatus for ansatte')}
              onChange={(e) => onFilter(e.target.value as PeopleFilter)}
            >
              <option value="alle">{T('Alle statuser')}</option>
              <option value="gjenstar">{T('Gjenstående arbeid')}</option>
              <option value="ferdig">{T('Ferdig kontrollert')}</option>
            </select>
          </label>
          <label>
            <span className="sr">{T('Sorter ansatte')}</span>
            <select
              className="inp kps"
              value={sort}
              data-testid="people-sort"
              aria-label={T('Sorter ansatte')}
              onChange={(e) => onSort(e.target.value as 'prioritet' | 'navn')}
            >
              <option value="prioritet">{T('Mest å avklare')}</option>
              <option value="navn">{T('Navn')}</option>
            </select>
          </label>
        </div>
      </div>
      {!rows.length ? (
        <section className="kempty">
          <Icon name={filter === 'ferdig' ? 'clock' : 'check'} size={28} />
          <h2>
            {!all.length
              ? T('Ingen ansatte med registreringer i utvalget')
              : filter === 'ferdig'
                ? T('Ingen ansatte er ferdig kontrollert ennå')
                : T('Alle ansatte i utvalget er ferdig kontrollert')}
          </h2>
          <p>
            {!all.length
              ? T('Velg en annen periode, eller fjern team og søk.')
              : filter === 'ferdig'
                ? T('Godkjenn registreringene og gjør rede for avsluttede vakter før en ansatt er ferdig.')
                : T('Du kan se godkjente registreringer under alle ansatte.')}
          </p>
          {filter !== 'alle' && (
            <div>
              <button type="button" className="btn pri" onClick={() => onFilter('alle')}>
                {T('Vis alle ansatte')}
              </button>
            </div>
          )}
        </section>
      ) : (
        <>
          <div className="ktablewrap">
            <table className="mt kptable" data-testid="people-table">
              <caption className="sr">{T('Kontroll per ansatt. Antall viser registreringer.')}</caption>
              <thead>
                <tr>
                  <th scope="col">{T('Ansatt')}</th>
                  <th scope="col">{T('Trenger vurdering')}</th>
                  <th scope="col">{T('Klare til godkjenning')}</th>
                  <th scope="col">{T('Godkjent')}</th>
                  <th scope="col">
                    <span className="sr">{T('Handling')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.map((p) => (
                  <tr key={p.id} data-testid="people-row">
                    <th scope="row">
                      <PersonLabel id={p.id} name={nameOf(p.id)}>
                        <b>{nameOf(p.id)}</b>
                        <small>{subOf(p.id)}</small>
                      </PersonLabel>
                      {(p.missing > 0 || p.waiting > 0 || p.ongoing > 0 || p.complete) && (
                        <span className="kxtra">
                          {p.missing > 0 && (
                            <span className="kfact missing">
                              <Icon name="warn" size={14} />
                              {T('{n} uten stempling', { n: p.missing })}
                            </span>
                          )}
                          {p.waiting > 0 && (
                            <span className="kfact">
                              <Icon name="clock" size={14} />
                              {T('{n} venter på svar', { n: p.waiting })}
                            </span>
                          )}
                          {p.ongoing > 0 && (
                            <span className="kfact">
                              <Icon name="clock" size={14} />
                              {T('{n, plural, one {# vakt pågår nå} other {# vakter pågår nå}}', {
                                n: p.ongoing,
                              })}
                            </span>
                          )}
                          {p.complete && (
                            <span className="kfact kdone">
                              <Icon name="check" size={14} />
                              {T('Ferdig kontrollert')}
                            </span>
                          )}
                        </span>
                      )}
                    </th>
                    <td data-k={T('Trenger vurdering')}>
                      {count(p, 'handling', p.review, T('trenger vurdering'), 'needs')}
                    </td>
                    <td data-k={T('Klare til godkjenning')}>
                      {count(p, 'klare', p.ready, T('klare til godkjenning'), 'ready')}
                    </td>
                    <td data-k={T('Godkjent')}>
                      {count(p, 'godkjent', p.approved, T('godkjente registreringer'), 'approved', p.total)}
                    </td>
                    <td className="kopen">
                      <button
                        type="button"
                        className="btn ghost"
                        onClick={() => onDrill(p.id, 'alle')}
                        aria-label={T('Åpne registreringene til {name}', { name: nameOf(p.id) })}
                      >
                        {T('Åpne')}
                        <Icon name="chevronRight" size={19} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination count={rows.length} page={pg} size={PEOPLE_PAGE} onPage={onPage} />
          <p className="kdef">
            {T(
              'Ferdig kontrollert: registreringene er godkjent, ingen vakter pågår, og avsluttede planlagte vakter har en registrering. Lønn og overføring kontrolleres i Rapporter.',
            )}
          </p>
        </>
      )}
      {worktime && (
        <p className="kfoot">
          <Link className="mlink" to={worktime}>
            {T('Arbeidstid per person')}
            <Icon name="arrowRight" size={16} />
          </Link>{' '}
          {T('Stemplinger, timer i perioden og historikk for hver ansatt.')}
        </p>
      )}
    </section>
  );
}
